import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { OrderListSkeleton } from '../components/Skeleton';
import ErrorState from '../components/ErrorState';
import EmptyState from '../components/EmptyState';
import usePageMetadata from '../hooks/usePageMetadata';
import * as ordersApi from '../api/endpoints/orders';
import { containerNarrow, focusRing, pageHeading, pageSection } from '../styles/ui';

const OrderHistoryPage = () => {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });

  usePageMetadata({
    title: 'Your Orders | Fresh & Homely',
    description: 'View your Fresh & Homely order history.',
    canonicalPath: '/orders',
  });

  const load = () => {
    setState({ status: 'loading', data: null, error: null });
    ordersApi
      .listOrders({ status: 'all', limit: 50 })
      .then((response) => setState({ status: 'success', data: response.data, error: null }))
      .catch((err) => setState({ status: 'error', data: null, error: err.message }));
  };

  useEffect(load, []);

  return (
    <>
      <Navbar />
      <section className={pageSection}>
        <div className={containerNarrow}>
          <h1 className={`${pageHeading} mb-8 sm:mb-10`}>Your Orders</h1>

          {state.status === 'loading' && <OrderListSkeleton count={4} />}
          {state.status === 'error' && <ErrorState message={state.error} onRetry={load} />}
          {state.status === 'success' && state.data.length === 0 && (
            <EmptyState
              icon={Package}
              title="No orders yet"
              description="Once you place an order, it'll show up here."
              action={{ label: 'Shop Categories', to: '/categories' }}
            />
          )}
          {state.status === 'success' && state.data.length > 0 && (
            <div className="space-y-4">
              {state.data.map((order) => (
                <Link
                  key={order._id || order.saleId}
                  to={`/orders/${order._id || order.saleId}`}
                  className={`flex items-center gap-4 bg-white rounded-2xl border border-emerald-100 shadow-card p-5 card-hover ${focusRing}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                    <Package size={20} className="text-emerald-700" aria-hidden="true" />
                  </div>
                  <div className="flex-1 flex flex-wrap items-center justify-between gap-2 min-w-0">
                    <div className="min-w-0">
                      <p className="font-bold text-emerald-950 truncate">{order.invoiceNumber || `Order ${order._id}`}</p>
                      <p className="text-sm text-ink-secondary">
                        {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : ''}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold text-emerald-950">₹{order.grandTotal}</p>
                      {(() => {
                        const status = order.fulfillment?.status || order.status || 'placed';
                        const isFailureStatus = ['failed', 'cancelled'].includes(status);
                        return (
                          <span
                            className={`text-xs font-medium capitalize px-2.5 py-1 rounded-full ${
                              isFailureStatus ? 'text-red-700 bg-red-50' : 'text-success-700 bg-success-50'
                            }`}
                          >
                            {status.replace(/_/g, ' ')}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
};

export default OrderHistoryPage;
